import React, { useState, useCallback, useEffect, useRef } from 'react';
import { ScrollView } from 'react-native';
import {
    Background,
    OptionsSelectBox,
    ActivityIndicator,
    useLanguageSettingsContext
} from 'LocalGluons';
import { t } from 'Locale';
import { useStore } from 'Store';
import NavigationService from '../../Navigation/navigationService';

import defaultStyles from './LanguageSettings.jss';
import { useAppErrorHandler } from '../../App/AppErrorHandlerContext/AppErrorHandlerContext';
import { useNavigationPropsContext } from '../../Navigation/navigationPropsContext';
import { useAppEntryContext } from 'Pages/AppEntry';

const SUBTITLE_OFF_VALUE_FROM_API = '--';

// TODO: change it to dynamic when support on mcore API
const APP_LANGUAGE_MAP = {
    EN: 'English',
    CZ: '\u010c\u0065\u0161\u0074\u0069\u006e\u0061', // Čeština
    SK: '\u0053\u006c\u006f\u0076\u0065\u006e\u010d\u0069\u006e\u0061' // Slovenčina
};

const handleLanguageMismatch = ({ app, audio }, setError) => {
    const isCurrentAppLanguageValidOption = app.options.includes(app.current);
    const isCurrentAudioLanguageValidOption = audio || audio.options.includes(audio.current);
    if (!isCurrentAppLanguageValidOption || !isCurrentAudioLanguageValidOption) {
        setError({
            error: { message: t('ERROR.UNKNOWN') },
            screenId: 'languageSettings',
            closeErrorModalCallback: () =>
                NavigationService.getTopLevelNavigationInstance().navigate('AppEntry')
        });
        const missingOnes = [
            !isCurrentAppLanguageValidOption ? 'App' : false,
            !isCurrentAudioLanguageValidOption ? 'Audio' : false
        ].filter(Boolean);
        throw new Error(`Unable to find current ${missingOnes.join(', ')} language in options`);
    }
};

const LanguageSettings = () => {
    const { appManager, setLanguage, userData } = useStore();
    const { setError } = useAppErrorHandler();
    const { setPreferredLangSettings } = useLanguageSettingsContext();
    const { updateNavigationProps } = useNavigationPropsContext();
    const [configLoaded, setConfigLoaded] = useState(false);
    const [data, setData] = useState({
        appLanguage: [],
        audioLanguages: []
    });
    const currentAppLanguage = useRef('');
    const defaultSubtitleLanguage = useRef('');
    const [appLanguage, setAppLanguage] = useState('');
    const [preferredAudio, setPreferredAudio] = useState('');
    const audioLanguagesRef = useRef([]);
    const navigation = NavigationService.getTopLevelNavigationInstance();
    const { initialize } = useAppEntryContext();

    const handleLanguageMismatches = (
        isLoggedIn,
        applicationLanguage,
        stringModifications,
        preferredAudioLanguage,
        audioLanguages
    ) => {
        if (isLoggedIn) {
            handleLanguageMismatch(
                {
                    app: {
                        current: applicationLanguage,
                        options: stringModifications.map((entry) => entry.language)
                    },
                    audio: {
                        current: preferredAudioLanguage,
                        options: audioLanguages.map((entry) => entry.name)
                    }
                },
                setError
            );
        } /* else {
            handleLanguageMismatch(
                {
                    app: {
                        current: applicationLanguage,
                        options: stringModifications.map((entry) => entry.language)
                    }
                },
                setError
            );
        } */
    };

    useEffect(() => {
        const fetchLanguageSettings = async () => {
            try {
                const { api } = appManager;
                const languageSettings = await api.fetchLanguage();
                const response = await api.fetchMCoreConfig();
                const { stringModifications = [] } = response;

                const { applicationLanguage, preferredAudioLanguage, preferredSubtitlesLanguage } =
                    languageSettings;

                setAppLanguage(applicationLanguage);
                currentAppLanguage.current = applicationLanguage;

                setPreferredAudio(preferredAudioLanguage);
                const audioLanguages = (await api.fetchAudioLanguages()) || [];

                const subtitles = await api.fetchSubtitleLanguages();
                const defaultSubtitlesValue = subtitles?.find(({ playerValues }) =>
                    playerValues?.find((lng) => lng === SUBTITLE_OFF_VALUE_FROM_API)
                );
                defaultSubtitleLanguage.current =
                    defaultSubtitlesValue?.name || preferredSubtitlesLanguage;

                handleLanguageMismatches(
                    Boolean(userData),
                    applicationLanguage,
                    stringModifications,
                    preferredAudioLanguage,
                    audioLanguages
                );

                setData({
                    appLanguage: stringModifications.map(({ language }) => ({
                        id: language,
                        label: APP_LANGUAGE_MAP[language] || language
                    })),
                    audioLanguages: audioLanguages.map(({ name }) => ({
                        id: name,
                        label: name
                    }))
                });

                audioLanguagesRef.current = audioLanguages;
                // Set pref settings for player
                setPreferredLangSettings(languageSettings, audioLanguages);
            } catch (err) {
                console.log(err);
            }
            setConfigLoaded(true);
        };

        fetchLanguageSettings();
    }, []);

    const onRefresh = useCallback(async () => {
        await appManager.resetTranslations();
        initialize();
        const lang = await appManager.getAppPreferredLanguage();
        setLanguage(appLanguage);
        updateNavigationProps({
            currentAppLanguage: lang
        });
    }, []);

    useEffect(() => {
        if (configLoaded) {
            onRefresh();
        }
    }, [appLanguage]);

    useEffect(() => {
        const updateLanguageSettings = async () => {
            try {
                const languageSettings = {
                    applicationLanguage: appLanguage,
                    preferredAudioLanguage: preferredAudio,
                    preferredSubtitlesLanguage: defaultSubtitleLanguage.current
                };
                await appManager.api.setLanguages(languageSettings);

                // Set pref settings for player
                setPreferredLangSettings(languageSettings, audioLanguagesRef.current);
                // Reset whole app on language change
                if (currentAppLanguage.current !== appLanguage) {
                    await onRefresh();
                }
            } catch (err) {
                console.log(err);
            }
        };

        // Prevent update when data are not loaded yet
        if (configLoaded) {
            updateLanguageSettings();
        }
    }, [appLanguage, preferredAudio]);

    const appLanguageCallback = useCallback((itemId) => {
        navigation.navigate('home', {
            showSplash: true
        });
        setAppLanguage(itemId);
    }, []);

    const preferredAudioCallback = useCallback((itemId) => {
        setPreferredAudio(itemId);
    }, []);

    return (
        <Background>
            {configLoaded ? (
                <ScrollView style={defaultStyles.container}>
                    <OptionsSelectBox
                        heading={t('SETTINGS.LANGUAGE_APP')}
                        onChange={appLanguageCallback}
                        items={data.appLanguage}
                        selected={appLanguage}
                        testID="app_language_select"
                    />

                    {/* <OptionsSelectBox
                        heading={t('SETTINGS.LANGUAGE_AUDIO')}
                        onChange={preferredAudioCallback}
                        items={data.audioLanguages}
                        selected={preferredAudio}
                        testID="audio_language_select"
                    /> */}
                    {/* <Text style={defaultStyles.note}>
                        // just space in the string ' '
                        {t('SETTINGS.LANGUAGE_AUDIO_DESCRIPTION')}
                    </Text> */}
                </ScrollView>
            ) : (
                <ActivityIndicator />
            )}
        </Background>
    );
};

export { LanguageSettings };
